import { useState } from "react";
import { aiJobKind } from "@reina/shared";
import {
  EmptyState,
  Field,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  StatCard,
  TableWrap,
} from "../../components";
import { useAiJobs } from "../../api/system";
import { errorMessage, formatDateTime, formatInt, formatUsd } from "../../lib/format";
import { aiJobStatusTone } from "../../lib/tones";

export function AiUsagePage() {
  const [kind, setKind] = useState("");
  const jobs = useAiJobs({ ...(kind ? { kind } : {}), limit: 500 });
  const items = jobs.data?.items ?? [];

  const totalCost = items.reduce((sum, job) => sum + (job.costUsd ?? 0), 0);
  const totalIn = items.reduce((sum, job) => sum + (job.inputTokens ?? 0), 0);
  const totalOut = items.reduce((sum, job) => sum + (job.outputTokens ?? 0), 0);
  const failed = items.filter((job) => job.status === "failed").length;

  return (
    <div className="stack">
      <div className="grid-4">
        <StatCard
          label="Total cost"
          value={jobs.data ? formatUsd(totalCost) : "…"}
          sub={kind ? `for ${kind} jobs` : "across all logged jobs"}
        />
        <StatCard
          label="Jobs"
          value={jobs.data ? formatInt(jobs.data.total) : "…"}
          sub="logged model calls"
        />
        <StatCard
          label="Tokens in / out"
          value={jobs.data ? `${formatInt(totalIn)} / ${formatInt(totalOut)}` : "…"}
          sub="prompt vs completion tokens"
        />
        <StatCard
          label="Failed"
          value={jobs.data ? String(failed) : "…"}
          sub="jobs that errored"
          {...(failed > 0 ? { tone: "danger" as const } : {})}
        />
      </div>
      <Panel>
        <PanelHead
          title="AI usage"
          sub="model calls, tokens and cost per job — identical inputs are served from cache"
          actions={
            <Field label="Kind">
              <select
                className="field"
                style={{ width: 220 }}
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              >
                <option value="">All kinds</option>
                {aiJobKind.options.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </Field>
          }
        />
        {jobs.isPending ? (
          <PanelBody>
            <EmptyState title="Loading AI jobs…" />
          </PanelBody>
        ) : jobs.isError ? (
          <PanelBody>
            <EmptyState title="Could not load AI jobs" hint={errorMessage(jobs.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No AI jobs yet"
              hint="PDF parses, AI classification and buy-list matching are logged here."
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Kind</th>
                <th>Status</th>
                <th>Model</th>
                <th>Tokens in</th>
                <th>Tokens out</th>
                <th>Cost</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((job) => (
                <tr key={job.id}>
                  <td className="mono">{job.kind}</td>
                  <td>
                    <Pill tone={aiJobStatusTone[job.status]} title={job.error ?? undefined}>
                      {job.status}
                    </Pill>
                  </td>
                  <td className="mono">{job.model || "—"}</td>
                  <td className="mono">{formatInt(job.inputTokens)}</td>
                  <td className="mono">{formatInt(job.outputTokens)}</td>
                  <td className="money">{formatUsd(job.costUsd)}</td>
                  <td className="mono">{formatDateTime(job.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
    </div>
  );
}
